import { effectiveValue, evaluatePolicy } from "./document.js";

const statusOrder = { "non-compliant": 0, warning: 1, "on-target": 2, compliant: 2, "not-evaluated": 3 };

export function evaluateCompliance(document) {
  return (document.policies ?? []).flatMap((policy, policyIndex) => {
    if (policy.enabled === false) return [];
    const results = evaluatePolicy(document, policy).map((result) => ({
      ...result,
      subject: resultSubject(document, policy, result)
    }));
    return [{ policy, policyIndex, results, status: worstComplianceStatus(results) }];
  });
}

export function complianceGap(evaluations) {
  return evaluations.reduce((gap, evaluation) => evaluation.results.reduce((policyGap, result) => {
    const mandatory = distanceOutside(result.basisPoints, evaluation.policy.must);
    const preferred = evaluation.policy.should ? distanceOutside(result.basisPoints, evaluation.policy.should) : mandatory;
    return {
      mandatory: policyGap.mandatory + mandatory,
      advisory: policyGap.advisory + Math.max(preferred - mandatory, 0)
    };
  }, gap), { mandatory: 0, advisory: 0 });
}

export function assetComplianceImpacts(document, evaluations) {
  const impacts = new Map(document.assets.map((asset) => [asset.id, { violations: 0, warnings: 0 }]));
  const allocations = new Map((document.allocations ?? []).map((allocation) => [allocation.assetId, allocation.basisPoints]));

  for (const evaluation of evaluations) {
    for (const result of evaluation.results) {
      const key = impactKey(result, evaluation.policy);
      if (!key) continue;
      for (const assetId of new Set(resultAssetIds(result))) {
        if ((allocations.get(assetId) ?? 0) === 0) continue;
        const impact = impacts.get(assetId);
        if (impact) impact[key] += 1;
      }
    }
  }

  return impacts;
}

export function worstComplianceStatus(results) {
  if (!results.length) return "not-evaluated";
  return results.reduce((worst, result) => (
    complianceStatusRank(result.status) < complianceStatusRank(worst) ? result.status : worst
  ), results[0].status);
}

function complianceStatusRank(status) {
  return statusOrder[status] ?? statusOrder["not-evaluated"];
}

function distanceOutside(basisPoints, bounds) {
  return Math.max((bounds.minimum ?? 0) - basisPoints, basisPoints - (bounds.maximum ?? 10000), 0);
}

function impactKey(result, policy) {
  if (result.status === "non-compliant" && improvesWhenReduced(result.basisPoints, policy.must)) return "violations";
  if (result.status === "warning" && improvesWhenReduced(result.basisPoints, policy.should)) return "warnings";
  return null;
}

function improvesWhenReduced(basisPoints, bounds) {
  return bounds?.maximum !== undefined && basisPoints > bounds.maximum;
}

function resultAssetIds(result) {
  if (result.subject.type === "asset") return [result.subject.asset.id];
  return result.subject.assetIds;
}

function resultSubject(document, policy, result) {
  if (policy.scope.type === "specific") {
    return { type: "property-value", propertyId: policy.scope.propertyId, valueId: policy.scope.valueId, label: result.label, assetIds: propertyValueAssetIds(document, policy.scope.propertyId, policy.scope.valueId) };
  }
  const dimension = policy.scope.dimension;
  if (dimension.type === "asset") {
    return { type: "asset", asset: document.assets.find((asset) => asset.id === result.key) };
  }
  return { type: "property-value", propertyId: dimension.propertyId, valueId: result.key, label: result.label, assetIds: propertyValueAssetIds(document, dimension.propertyId, result.key) };
}

function propertyValueAssetIds(document, propertyId, valueId) {
  return document.assets.filter((asset) => effectiveValue(document, asset.id, propertyId).id === valueId).map((asset) => asset.id);
}
